import { useCallback, useEffect, useMemo, useState } from "react";
import { useRetainedValue } from "../hooks/useRetainedValue.js";
import { LayoutGrid, RotateCcw, SlidersVertical } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
  type ScreenReaderInstructions,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { WIDTH_TO_COLS, type WidgetKind } from "@playbookai/types";
import { Button } from "../atoms/Button/Button.js";
import { Tooltip } from "../molecules/Tooltip/index.js";
import { cn } from "../lib/utils.js";
import { getWidget } from "../widgets/registry.js";
import { AddWidgetMenu } from "./entity-dashboard/AddWidgetMenu.js";
import { OverlayCard } from "./entity-dashboard/OverlayCard.js";
import { SortableCard } from "./entity-dashboard/SortableCard.js";
import {
  WidgetErrorBoundary,
  WidgetSlot,
} from "./entity-dashboard/WidgetErrorBoundary.js";
import { WidgetEditDialog } from "./entity-dashboard/WidgetEditDialog.js";
import {
  COL_SPAN_CLASS,
  reorderItems,
  widgetLabel,
} from "./entity-dashboard/helpers.js";
import type {
  DashboardItem,
  EntityDashboardProps,
} from "./entity-dashboard/types.js";

export type {
  DashboardItem,
  EntityDashboardProps,
} from "./entity-dashboard/types.js";

/* ---------------- EntityDashboard (PB-177, ADR-0015) ----------------
   Generic, data-blind configurable dashboard. Layout is a prop; every
   mutation is a callback; widget bodies render through the `renderWidget`
   slot (the dashboard never fetches or stores). Persistence is the host's
   job — there is no `localStorage` here (the oracle's storage.ts is
   deliberately dropped, mirroring the AppLauncher verdict in ADR-0014).

   Drag-reorder is a width-aware 12-column grid built directly on @dnd-kit
   (`rectSortingStrategy` + DragOverlay + position-aware announcements). The
   `SortableList` molecule is vertical-list-only and doesn't fit, so we reuse
   only its pure `reorderIds` helper. An explicit kebab "Move forward / Move
   back" pair is the keyboard-friendly, discoverable companion to the grip
   (the PB-181 precedent). Every affordance renders only when its callback is
   supplied. The per-widget edit dialog is a separate follow-up (PR-B).

   This file is the orchestration shell; the chrome / cards / menu / boundary /
   helpers live in `./entity-dashboard/` (DS one-component-per-file convention). */

export function EntityDashboard({
  layout,
  catalog,
  renderWidget,
  renderPreview,
  onReorder,
  onChangeWidth,
  onAddWidget,
  onRemoveWidget,
  onEditWidget,
  onReset,
  editMode: editModeProp,
  onEditModeChange,
  toolbarLabel,
  variant = "default",
  emptyState,
  className,
}: EntityDashboardProps) {
  const [internalEdit, setInternalEdit] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isControlled = editModeProp !== undefined;
  const editMode = isControlled ? !!editModeProp : internalEdit;
  const setEditMode = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalEdit(next);
      onEditModeChange?.(next);
      if (!next) setEditingId(null);
    },
    [isControlled, onEditModeChange],
  );

  // Keep the reordered grid in its destination while the host cancels any
  // in-flight query and publishes its optimistic cache update. Without this
  // short-lived visual layout, DragOverlay measures the old source slot and
  // animates back to it before the authoritative layout arrives.
  const [visualLayout, setVisualLayout] = useState<{
    source: readonly DashboardItem[];
    items: DashboardItem[] | null;
  }>({ source: layout, items: null });
  if (visualLayout.source !== layout) {
    setVisualLayout({ source: layout, items: null });
  }
  const renderedLayout =
    visualLayout.source === layout && visualLayout.items
      ? visualLayout.items
      : layout;

  const ids = useMemo(
    () => renderedLayout.map((i) => i.instanceId),
    [renderedLayout],
  );
  const canReorder = !!onReorder;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const activeItem = activeId
    ? (renderedLayout.find((i) => i.instanceId === activeId) ?? null)
    : null;
  // Gated on editMode so a controlled host flipping editMode→false directly
  // (without the toggle) also closes the dialog, not just the toggle path.
  const editingItem =
    editMode && editingId
      ? (renderedLayout.find((i) => i.instanceId === editingId) ?? null)
      : null;
  // Kept for the dialog's closing frame, when the selection is already gone.
  const editing = useRetainedValue(editingItem);

  const placedSingletons = useMemo(() => {
    const set = new Set<WidgetKind>();
    for (const it of renderedLayout) {
      const meta = getWidget(it.kind)?.dashboard;
      if (meta && !meta.allowMultiple) set.add(it.kind);
    }
    return set;
  }, [renderedLayout]);

  const commitReorder = useCallback(
    (activeKey: string, overKey: string) => {
      if (!onReorder) return;
      const next = reorderItems(renderedLayout, activeKey, overKey);
      if (next !== renderedLayout) {
        const items = [...next];
        setVisualLayout({ source: layout, items });
        const clearPendingOrder = () => {
          setVisualLayout((current) =>
            current.source === layout && current.items === items
              ? { source: layout, items: null }
              : current,
          );
        };
        try {
          const pending = onReorder(items);
          if (pending !== undefined) {
            void pending.then(clearPendingOrder, clearPendingOrder);
          }
        } catch (error) {
          clearPendingOrder();
          throw error;
        }
      }
    },
    [layout, onReorder, renderedLayout],
  );

  const move = useCallback(
    (instanceId: string, dir: -1 | 1) => {
      const idx = ids.indexOf(instanceId);
      const targetIdx = idx + dir;
      const targetId = ids[targetIdx];
      if (idx < 0 || targetId === undefined) return;
      commitReorder(instanceId, targetId);
    },
    [ids, commitReorder],
  );

  const clearDrag = useCallback(() => {
    setActiveId(null);
    if (typeof document !== "undefined") {
      document.body.classList.remove("is-dragging");
    }
  }, []);

  const handleDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
    if (typeof document !== "undefined") {
      document.body.classList.add("is-dragging");
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    try {
      if (over && active.id !== over.id) {
        commitReorder(String(active.id), String(over.id));
      }
    } finally {
      clearDrag();
    }
  };

  useEffect(
    () => () => {
      if (typeof document !== "undefined") {
        document.body.classList.remove("is-dragging");
      }
    },
    [],
  );

  const nameOf = useCallback(
    (id: string) => {
      const it = renderedLayout.find((i) => i.instanceId === id);
      return it ? widgetLabel(it) : "widget";
    },
    [renderedLayout],
  );

  const announcements: Announcements = useMemo(
    () => ({
      onDragStart: ({ active }) => {
        const pos = ids.indexOf(String(active.id)) + 1;
        return `Picked up ${nameOf(String(active.id))}, position ${pos} of ${ids.length}.`;
      },
      onDragOver: ({ active, over }) => {
        if (!over) return undefined;
        const pos = ids.indexOf(String(over.id)) + 1;
        return `${nameOf(String(active.id))} moved to position ${pos} of ${ids.length}.`;
      },
      onDragEnd: ({ active, over }) => {
        if (!over) return `${nameOf(String(active.id))} dropped.`;
        const pos = ids.indexOf(String(over.id)) + 1;
        return `${nameOf(String(active.id))} dropped at position ${pos} of ${ids.length}.`;
      },
      onDragCancel: ({ active }) =>
        `Reordering ${nameOf(String(active.id))} cancelled.`,
    }),
    [ids, nameOf],
  );

  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable:
      "To reorder a widget, focus its drag handle and press Space or Enter to pick it up, the arrow keys to move it, and Space or Enter to drop. Press Escape to cancel. Each widget's menu also has Move forward and Move back.",
  };

  const hasEditAffordances =
    canReorder ||
    !!onChangeWidth ||
    !!onAddWidget ||
    !!onRemoveWidget ||
    !!onEditWidget ||
    !!onReset;
  const focused = variant === "focused";
  const gridClassName = cn(
    "pb-entity-dashboard-grid grid grid-cols-12 auto-rows-min",
    focused ? "gap-4 lg:gap-5" : "gap-3",
  );

  return (
    <div
      className={cn(
        /* The dashboard is the size container its card spans query
           (PB-5436): the Player AI side rail narrows a host page without
           changing the window, so window breakpoints kept four cards across
           in half the room. Named, so a host's own container (LAMA's
           /content) is never matched by accident. */
        "@container/dashboard",
        className ?? (focused ? "p-0" : "p-3"),
        focused && "pb-entity-dashboard-focused",
      )}
    >
      {hasEditAffordances && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-2 border-b",
            focused
              ? "mb-5 border-pb-border/70 px-1 pb-3"
              : "mb-2 border-pb-border px-1 pb-2",
          )}
        >
          <div
            className={cn(
              "flex min-w-0 items-center gap-2",
              focused && "shrink-0",
            )}
          >
            {focused ? (
              <div className="truncate text-sm font-medium text-pb-text-secondary">
                {editMode
                  ? "Editing your dashboard"
                  : (toolbarLabel ?? "Your dashboard")}
              </div>
            ) : (
              <>
                <div className="text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold">
                  {editMode ? "Editing dashboard" : "Dashboard"}
                </div>
                {toolbarLabel && (
                  <div className="text-dense-2 text-pb-text-muted truncate">
                    · {toolbarLabel}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="ms-auto flex shrink-0 items-center gap-1.5">
            {editMode && onReset && (
              <Tooltip content="Reset to the default layout">
                <Button size="sm" variant="ghost" onClick={onReset}>
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </Button>
              </Tooltip>
            )}
            {editMode && onAddWidget && (
              <AddWidgetMenu
                catalog={catalog}
                placedSingletons={placedSingletons}
                onAdd={onAddWidget}
              />
            )}
            <Button
              size="sm"
              variant={editMode ? "primary" : "ghost"}
              aria-pressed={editMode}
              onClick={() => setEditMode(!editMode)}
              /* Stable semantic handle for the onboarding tour's "Make Home
                 yours" step. On the control that enters edit mode, because
                 that is what the step is asking the reader to find. */
              data-tour-id="home.customize"
            >
              {editMode ? (
                "Done"
              ) : (
                <>
                  <SlidersVertical className="w-3.5 h-3.5" /> Customize
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {renderedLayout.length === 0 ? (
        <div>
          {emptyState ?? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-pb-text-muted">
              <LayoutGrid className="w-6 h-6" aria-hidden />
              <p className="text-sm">No widgets yet.</p>
            </div>
          )}
        </div>
      ) : !editMode ? (
        <div className={gridClassName}>
          {renderedLayout.map((item) => (
            <div
              key={item.instanceId}
              data-widget-kind={item.kind}
              data-widget-width={item.width}
              className={cn(
                "min-w-0",
                COL_SPAN_CLASS[WIDTH_TO_COLS[item.width]],
              )}
            >
              <WidgetErrorBoundary
                widgetName={widgetLabel(item)}
                resetKeys={[item.instanceId, item.params]}
              >
                <WidgetSlot item={item} renderWidget={renderWidget} />
              </WidgetErrorBoundary>
            </div>
          ))}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          {...(canReorder
            ? { accessibility: { announcements, screenReaderInstructions } }
            : {})}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={clearDrag}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <div className={gridClassName}>
              {renderedLayout.map((item, idx) => (
                <SortableCard
                  key={item.instanceId}
                  item={item}
                  index={idx}
                  total={renderedLayout.length}
                  canReorder={canReorder}
                  renderWidget={renderWidget}
                  onChangeWidth={onChangeWidth}
                  onRemove={onRemoveWidget}
                  onMove={canReorder ? move : undefined}
                  onEdit={onEditWidget ? setEditingId : undefined}
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeItem ? (
              <OverlayCard item={activeItem} renderWidget={renderWidget} />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* `onEditWidget` is a capability — without it there is no editing at
          all — so it still gates the mount. The SELECTION is the open state and
          moves to `open`, with the item retained so the dialog keeps rendering
          what it was editing while it closes (ADR-0091). The `key` is what
          reseeds the form when the selection moves straight from one widget to
          another, since `open` never falls in that path; reopening the SAME
          widget keeps this instance, and the dialog reseeds on its own open
          edge. */}
      {onEditWidget && editing && (
        <WidgetEditDialog
          key={editing.instanceId}
          open={editingItem !== null}
          item={editing}
          renderWidget={renderPreview ?? renderWidget}
          onClose={() => setEditingId(null)}
          onSave={(next) => onEditWidget(editing.instanceId, next)}
        />
      )}
    </div>
  );
}
